import { API_ENDPOINTS } from "@/constants/api";
import { http } from "@/lib/http-client";
import type {
  ObsChartOptions,
  ObsCreateReportInput,
  ObsReport,
  ObsTileResult,
  ObsWorkbookProfile,
} from "@/types/obs";

/**
 * OBS report API — a report's identity, tile and saved column mapping.
 *
 * Separate from `obsService`, which moves DATA (workbook in, dataset out). This
 * one is about what a report IS: its slug, its title, the tile that links to it,
 * and the mapping every future upload for it is validated against.
 *
 * ⚠️ Like `/api/obs-datasets`, these routes are deliberately absent from the
 * backend's ENVELOPE_PREFIXES: the response envelope discards the body on any
 * status >= 400, and a rejected create needs to say WHICH field was wrong.
 * Responses therefore arrive RAW — no `unwrapData` — and a Strapi error body
 * (`{data:null, error:{...}}`) has to be told apart from a real answer rather
 * than rendering as an empty, reassuring result.
 */

interface StrapiErrorBody {
  error?: { status?: number; name?: string; message?: string };
}

function throwIfStrapiError(payload: unknown): void {
  const err = (payload as StrapiErrorBody | null)?.error;
  if (err && (err.message || err.name)) {
    throw new Error(err.message || err.name || "فشل الطلب");
  }
}

function asReport(payload: unknown): ObsReport {
  throwIfStrapiError(payload);
  const report = payload as ObsReport | null;
  if (!report || typeof report.slug !== "string") {
    throw new Error("استجابة غير متوقعة من الخادم");
  }
  return report;
}

export const obsReportService = {
  /** Every report, the six first, then created ones in grid order. */
  async list(): Promise<ObsReport[]> {
    const response = await http.get(API_ENDPOINTS.OBS_REPORTS.LIST);
    throwIfStrapiError(response);
    return (Array.isArray(response) ? response : []) as ObsReport[];
  },

  /**
   * Read a workbook and propose what its columns mean.
   *
   * 🔴 A PROPOSAL IS NOT A MAPPING. Nothing is stored by this call and nothing is
   * decided by it: `budget` and `actual` are not inferable from a file, and the
   * currency and scale are not in one at all. The operator confirms every role
   * before any of it is saved.
   */
  async profile(file: File): Promise<ObsWorkbookProfile> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await http.upload(
      "POST",
      API_ENDPOINTS.OBS_REPORTS.PROFILE,
      formData,
    );
    throwIfStrapiError(response);
    const profile = response as ObsWorkbookProfile | null;
    if (!profile || !Array.isArray(profile.sheets)) {
      const message = (response as { error?: string } | null)?.error;
      throw new Error(message || "تعذّر قراءة الملف");
    }
    return profile;
  },

  async create(input: ObsCreateReportInput): Promise<ObsReport> {
    return asReport(await http.post(API_ENDPOINTS.OBS_REPORTS.CREATE, input));
  },

  async update(slug: string, patch: Partial<ObsReport>): Promise<ObsReport> {
    return asReport(
      await http.put(API_ENDPOINTS.OBS_REPORTS.UPDATE(slug), patch),
    );
  },

  /**
   * Attach the tile that links to this report from the OBS landing page.
   *
   * Goes through the governance API rather than Strapi's own upload endpoint:
   * this SPA holds an app-user Bearer token, which `/api/upload` does not accept.
   */
  async uploadTile(
    slug: string,
    file: File,
    locale: "ar" | "en" = "ar",
  ): Promise<ObsTileResult> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await http.upload(
      "POST",
      API_ENDPOINTS.OBS_REPORTS.TILE(slug, locale),
      formData,
    );
    throwIfStrapiError(response);
    return response as ObsTileResult;
  },

  /** What this report's current data can draw. Empty before the first upload. */
  async chartOptions(slug: string): Promise<ObsChartOptions> {
    const response = await http.get(
      API_ENDPOINTS.OBS_REPORTS.CHART_OPTIONS(slug),
    );
    throwIfStrapiError(response);
    return response as ObsChartOptions;
  },

  async remove(slug: string): Promise<void> {
    throwIfStrapiError(await http.delete(API_ENDPOINTS.OBS_REPORTS.DELETE(slug)));
  },
};
